import request from './request'
import { getMockCouponUsageListResponse, shouldUseCouponMock } from './couponCenterMock'
import {
  buildCouponCenterListParams,
  postCouponCenterList,
} from './CouponCenterList'
import { readCareOrderNumericId } from './orders'
import { getUserList } from './userslist'
import { normalizeMobileSearchKeyword } from '@/utils/userListDisplay'
import { normalizePhoneSearchDigits } from '@/utils/technicianListDisplay'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface CouponUsageLogListItem {
  id: number
  coupon_id?: string
  coupon_no?: string
  coupon_code?: string
  code?: string
  discount_amount?: number
  discount_amount_text?: string
  final_amount_paid?: number
  final_amount_paid_text?: string
  applied_order_id?: string
  user_mobile?: string
  user_id?: string
  usage_time?: string
  [key: string]: unknown
}

export interface CouponUsageLogListData {
  list: CouponUsageLogListItem[]
  pagination: Pagination
}

export interface CouponUsageLogListParams {
  page: number
  page_size: number
  search_type?: string
  keyword?: string
  usage_date?: string
}

export interface CouponUsageLogRow {
  id: string
  couponId: string
  couponCode: string
  discountAmount: string
  finalAmountPaid: string
  appliedOrderId: string
  orderPrimaryId?: number
  userId: string
  userMobile: string
  usageTime: string
}

const SEARCH_TYPE_ALIAS: Record<string, string> = {
  coupon_code: 'coupon_code',
  applied_order_id: 'applied_order_id',
  user_mobile: 'user_mobile',
}

function resolveSearchType(searchField: string): string {
  const key = searchField.trim()
  return SEARCH_TYPE_ALIAS[key] ?? key
}

function resolveMoneyText(text?: string, amount?: number): string {
  if (text?.trim()) return text.trim()
  if (typeof amount === 'number') return `$${amount.toFixed(2)}`
  return ''
}

export function buildCouponUsageLogParams(input: {
  page: number
  pageSize: number
  searchField: string
  searchQuery: string
  usageDate: string
}): CouponUsageLogListParams {
  return {
    page: input.page,
    page_size: input.pageSize,
    search_type: resolveSearchType(input.searchField),
    keyword: input.searchQuery.trim(),
    usage_date: input.usageDate.trim(),
  }
}

function readStringField(item: CouponUsageLogListItem, keys: string[]): string {
  for (const key of keys) {
    const value = item[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

function resolveCouponIdFromItem(item: CouponUsageLogListItem): string {
  return readStringField(item, [
    'coupon_id',
    'coupon_no',
    'coupon_code',
    'code',
  ])
}

function resolveCouponCodeFromItem(item: CouponUsageLogListItem): string {
  return readStringField(item, ['code', 'coupon_code', 'coupon_no', 'coupon_id'])
}

function resolveAppliedOrderId(item: CouponUsageLogListItem): string {
  return readStringField(item, [
    'applied_order_id',
    'order_id',
    'order_no',
    'order_code',
  ])
}

function resolveOrderPrimaryId(item: CouponUsageLogListItem): number | undefined {
  const candidates = [
    item.order_primary_id,
    item.applied_order_primary_id,
    item.care_order_id,
    item.order_db_id,
    item.care_order_primary_id,
  ]
  for (const candidate of candidates) {
    const id = readCareOrderNumericId(candidate)
    if (id != null) return id
  }
  return undefined
}

function resolveUserIdFromItem(item: CouponUsageLogListItem): string {
  if (typeof item.user_id === 'number' && Number.isFinite(item.user_id)) {
    return String(item.user_id)
  }

  const rawId = readStringField(item, ['user_id', 'user_no'])
  if (rawId && /^\d+$/.test(rawId)) return rawId

  return rawId
}

function resolveUserMobile(item: CouponUsageLogListItem): string {
  return readStringField(item, [
    'user_mobile',
    'mobile',
    'registered_mobile',
    'phone',
  ])
}

export function mapCouponUsageLogItem(item: CouponUsageLogListItem): CouponUsageLogRow {
  return {
    id: String(item.id),
    couponId: resolveCouponIdFromItem(item),
    couponCode: resolveCouponCodeFromItem(item),
    discountAmount: resolveMoneyText(item.discount_amount_text, item.discount_amount),
    finalAmountPaid: resolveMoneyText(item.final_amount_paid_text, item.final_amount_paid),
    appliedOrderId: resolveAppliedOrderId(item),
    orderPrimaryId: resolveOrderPrimaryId(item),
    userId: resolveUserIdFromItem(item),
    userMobile: resolveUserMobile(item),
    usageTime: item.usage_time?.trim() ?? '',
  }
}

function isLikelyCouponBusinessId(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  return /^COUPON/i.test(trimmed) || /^CPN/i.test(trimmed)
}

async function findCouponIdFromCenterList(couponCode: string): Promise<string> {
  const keyword = couponCode.trim()
  if (!keyword) return ''

  try {
    const response = await postCouponCenterList(
      buildCouponCenterListParams({
        page: 1,
        pageSize: 20,
        searchField: 'coupon_code',
        searchQuery: keyword,
        type: 'all',
        eligibleService: '',
        visibility: 'all',
        availability: 'all',
      }),
    )

    if (response.code !== 200 || !response.data?.list?.length) return ''

    const exact = response.data.list.find(
      (item) => item.code?.trim().toLowerCase() === keyword.toLowerCase(),
    )
    const hit = exact ?? response.data.list[0]
    if (!hit) return ''
    return hit.coupon_id?.trim() || String(hit.id)
  } catch (error) {
    console.error('Resolve coupon by code error:', error)
    return ''
  }
}

/**
 * 解析优惠券详情 coupon_id。
 * 优先直接返回业务 id，交给详情页只打一次 detail；仅在没有可用 id 时才查一次 list。
 */
export async function resolveCouponDetailId(row: CouponUsageLogRow): Promise<string> {
  const couponId = row.couponId.trim()
  const couponCode = row.couponCode.trim()

  if (isLikelyCouponBusinessId(couponId)) return couponId
  if (isLikelyCouponBusinessId(couponCode)) return couponCode
  if (couponId) return couponId

  if (couponCode) {
    const fromList = await findCouponIdFromCenterList(couponCode)
    if (fromList) return fromList
    return couponCode
  }

  return ''
}

function pickMatchedUserId(
  list: Array<{
    id: number | string
    user_id?: string
    user_no?: string
    registered_mobile?: string
    mobile?: string
    phone?: string
  }>,
  opts: { keyword?: string; mobileRaw?: string; matchUserId?: string },
): string {
  if (!list.length) return ''

  if (opts.matchUserId) {
    const key = opts.matchUserId.trim()
    const matched =
      list.find(
        (user) => user.user_id?.trim() === key || user.user_no?.trim() === key || String(user.id) === key,
      ) ?? list[0]
    return matched ? String(matched.id) : ''
  }

  const digitKeyword = normalizeMobileSearchKeyword(opts.mobileRaw ?? opts.keyword ?? '')
  const local10 = normalizePhoneSearchDigits(opts.mobileRaw ?? opts.keyword ?? '')
  const targetDigits = local10 || digitKeyword

  const matched =
    list.find((user) => {
      const phone = normalizeMobileSearchKeyword(
        user.registered_mobile || user.mobile || user.phone || '',
      )
      const phoneLocal = normalizePhoneSearchDigits(
        user.registered_mobile || user.mobile || user.phone || '',
      )
      return (
        !targetDigits ||
        phone === digitKeyword ||
        phone === targetDigits ||
        phoneLocal === local10 ||
        phone.endsWith(targetDigits) ||
        targetDigits.endsWith(phone) ||
        phoneLocal.endsWith(local10) ||
        local10.endsWith(phoneLocal)
      )
    }) ?? list[0]

  return matched ? String(matched.id) : ''
}

async function findUserPkByList(params: {
  search_type: 'mobile_no' | 'user_id'
  keyword: string
  mobileRaw?: string
  matchUserId?: string
}): Promise<string> {
  const keyword = params.keyword.trim()
  if (!keyword) return ''

  try {
    const response = await getUserList({
      page: 1,
      page_size: 10,
      search_type: params.search_type,
      keyword,
    })
    if (response.code !== 200 || !response.data?.list?.length) return ''
    return pickMatchedUserId(response.data.list, {
      keyword,
      mobileRaw: params.mobileRaw,
      matchUserId: params.matchUserId,
    })
  } catch (error) {
    console.error('Resolve user list error:', error)
    return ''
  }
}

/**
 * 用户详情需要数据库主键。
 * - 已是数字 id：不请求 list，直接交给 detail
 * - 业务号 / 手机号：最多只打 1 次 list
 */
export async function resolveUserDetailId(row: CouponUsageLogRow): Promise<string> {
  const userId = row.userId.trim()
  if (userId && /^\d+$/.test(userId)) {
    return userId
  }

  if (userId) {
    const fromUserId = await findUserPkByList({
      search_type: 'user_id',
      keyword: userId,
      matchUserId: userId,
    })
    if (fromUserId) return fromUserId
  }

  const mobile = row.userMobile.trim()
  if (mobile) {
    const keyword =
      normalizePhoneSearchDigits(mobile) ||
      normalizeMobileSearchKeyword(mobile) ||
      mobile
    const fromMobile = await findUserPkByList({
      search_type: 'mobile_no',
      keyword,
      mobileRaw: mobile,
    })
    if (fromMobile) return fromMobile
  }

  return ''
}

/** POST `/api/admin/coupon/usage-list` */
export async function postCouponUsageList(
  params: CouponUsageLogListParams,
): Promise<ApiResponse<CouponUsageLogListData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponUsageListResponse(params) as unknown as ApiResponse<CouponUsageLogListData>
  }
  return request.post<ApiResponse<CouponUsageLogListData>>('/api/admin/coupon/usage-list', params)
}
